/**
 * Queries e mutations de Compliance v4 — **API User** (Lambdas Gestão de Compliance
 * e Gestão de Documentos).
 *
 * Duas famílias de chave, e a separação é a regra:
 *  - **catálogo** (`padroes`, `estrutura`, `busca`) é GLOBAL — `chaveGlobal`. Um
 *    padrão vale para todos os órgãos (BR-23);
 *  - **questionário, evidências e sugestões** são POR CLIENTE — `chaveDeQuery`,
 *    com `idCliente` na frente e o `padraoId` logo depois. Trocar de cliente OU de
 *    padrão troca a chave, e o TanStack Query invalida em vez de mesclar.
 *
 * 🔒 BR-25: `useAnalisarSugestoes` é uma MUTATION — só dispara por clique.
 */
import { executarJobDeIa } from './aguardarExecucaoDiscoveryIa'
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { z } from 'zod'

import { apiUser } from '@/api/clients/apiUser'
import {
  estruturaSchema,
  evidenciaSchema,
  grupoSchema,
  itemQuestionarioSchema,
  manualSchema,
  padraoSchema,
  perguntaSchema,
  questionarioSchema,
  resultadoDeBuscaSchema,
  sugestaoSchema,
  urlDeDownloadSchema,
  urlDeUploadSchema,
  type Padrao,
  type Questionario,
} from '@/api/contracts/compliance'
import { enviarArquivo } from '@/api/upload'

import { chaveDeQuery, chaveGlobal } from './chaveDeQuery'

// ── Catálogo global ─────────────────────────────────────────────────────────
export function usePadroes() {
  return useQuery({
    queryKey: chaveGlobal('compliance-padroes'),
    queryFn: async () => z.array(padraoSchema).parse(await apiUser.get<unknown>('/compliance/padroes')),
  })
}

export function useEstrutura(padraoId: string | undefined) {
  return useQuery({
    queryKey: chaveGlobal('compliance-estrutura', padraoId),
    enabled: Boolean(padraoId),
    queryFn: async () =>
      estruturaSchema.parse(await apiUser.get<unknown>(`/compliance/padroes/${padraoId}/estrutura`)),
  })
}

/** O catálogo mudou: a estrutura, e tudo que é por cliente derivado dela (nota máxima). */
function invalidarCatalogo(client: QueryClient) {
  return Promise.all([
    client.invalidateQueries({ queryKey: chaveGlobal('compliance-padroes') }),
    client.invalidateQueries({ queryKey: chaveGlobal('compliance-estrutura') }),
    client.invalidateQueries({ queryKey: chaveGlobal('compliance-busca') }),
    client.invalidateQueries({
      predicate: (q) => q.queryKey[0] === 'cliente' && q.queryKey[2] === 'compliance-questionario',
    }),
  ])
}

export function useCriarPadrao() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (dados: { nome: string; ano: number; origem: Padrao['origem'] }) =>
      padraoSchema.parse(await apiUser.post<unknown>('/compliance/padroes', dados)),
    onSuccess: () => void invalidarCatalogo(client),
  })
}

/** Um manual de referência do padrão: upload-url → PUT S3 → registro. */
export async function enviarManual(padraoId: string, arquivo: File) {
  const enviado = await enviarArquivo(arquivo, async (meta) =>
    urlDeUploadSchema.parse(
      await apiUser.post<unknown>('/documentos/manuais/upload-url', { padraoId, ...meta }),
    ),
  )
  return manualSchema.parse(
    await apiUser.post<unknown>('/documentos/manuais', {
      padraoId,
      nome: arquivo.name,
      tipo: enviado.tipo,
      chave: enviado.chave,
      tamanhoBytes: enviado.tamanhoBytes,
    }),
  )
}

export function useCriarGrupo(padraoId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (dados: { paiId: string | null; codigo: string; titulo: string; peso?: number }) =>
      grupoSchema.parse(await apiUser.post<unknown>(`/compliance/padroes/${padraoId}/grupos`, dados)),
    onSuccess: () => void invalidarCatalogo(client),
  })
}

export function useAtualizarGrupo() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...campos }: { id: string; codigo?: string; titulo?: string; peso?: number }) =>
      grupoSchema.parse(await apiUser.patch<unknown>(`/compliance/grupos/${id}`, campos)),
    onSuccess: () => void invalidarCatalogo(client),
  })
}

export interface DadosDePergunta {
  grupoId: string
  codigo: string
  enunciado: string
  orientacaoIA: string | null
  opcoes: Array<{ rotulo: string; pontuacao: number }>
}

export function useSalvarPergunta(padraoId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...dados }: DadosDePergunta & { id: string | null }) =>
      perguntaSchema.parse(
        id
          ? await apiUser.patch<unknown>(`/compliance/perguntas/${id}`, dados)
          : await apiUser.post<unknown>(`/compliance/padroes/${padraoId}/perguntas`, dados),
      ),
    onSuccess: () => void invalidarCatalogo(client),
  })
}

export function useExcluirPergunta() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await apiUser.delete(`/compliance/perguntas/${id}`)
    },
    onSuccess: () => void invalidarCatalogo(client),
  })
}

/** Autocomplete de requisitos de `3c` — busca em TODOS os padrões. */
export function useBuscaDePerguntas(q: string) {
  const termo = q.trim()
  return useQuery({
    queryKey: chaveGlobal('compliance-busca', termo),
    enabled: termo.length > 0,
    queryFn: async () =>
      z
        .array(resultadoDeBuscaSchema)
        .parse(await apiUser.get<unknown>('/compliance/perguntas/busca', { params: { q: termo, limite: 20 } })),
  })
}

// ── Questionário (por cliente) ──────────────────────────────────────────────
function chaveDoQuestionario(idCliente: string, padraoId: string | undefined) {
  return chaveDeQuery(idCliente, 'compliance-questionario', padraoId)
}

export function useQuestionario(idCliente: string, padraoId: string | undefined) {
  return useQuery({
    queryKey: chaveDoQuestionario(idCliente, padraoId),
    enabled: Boolean(padraoId),
    queryFn: async () =>
      questionarioSchema.parse(
        await apiUser.get<unknown>(`/compliance/padroes/${padraoId}/questionario`, { params: { idCliente } }),
      ),
  })
}

/**
 * Resposta e observação da linha. Troca o item no cache com o que o servidor
 * devolveu e então invalida — os TOTAIS dependem da fórmula do servidor, e a
 * tela não recalcula nota.
 */
export function useSalvarResposta(idCliente: string, padraoId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (dados: { perguntaId: string; opcaoId: string | null; observacoes: string | null }) =>
      itemQuestionarioSchema.parse(
        await apiUser.put<unknown>(
          `/compliance/padroes/${padraoId}/respostas/${dados.perguntaId}`,
          { opcaoId: dados.opcaoId, observacoes: dados.observacoes },
          { params: { idCliente } },
        ),
      ),
    onSuccess: (item) => atualizarItem(client, idCliente, padraoId, item),
  })
}

export function useSalvarNotaParaIA(idCliente: string, padraoId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (dados: { perguntaId: string; nota: number | null }) =>
      itemQuestionarioSchema.parse(
        await apiUser.put<unknown>(
          `/compliance/padroes/${padraoId}/notas-ia/${dados.perguntaId}`,
          { nota: dados.nota },
          { params: { idCliente } },
        ),
      ),
    onSuccess: (item) => atualizarItem(client, idCliente, padraoId, item),
  })
}

function atualizarItem(
  client: QueryClient,
  idCliente: string,
  padraoId: string,
  item: Questionario['itens'][number],
) {
  const chave = chaveDoQuestionario(idCliente, padraoId)
  client.setQueryData<Questionario>(chave, (atual) =>
    atual
      ? { ...atual, itens: atual.itens.map((i) => (i.perguntaId === item.perguntaId ? item : i)) }
      : atual,
  )
  return client.invalidateQueries({ queryKey: chave })
}

export function useSugestoes(idCliente: string, padraoId: string | undefined) {
  return useQuery({
    queryKey: chaveDeQuery(idCliente, 'compliance-sugestoes', padraoId),
    enabled: Boolean(padraoId),
    queryFn: async () =>
      z
        .array(sugestaoSchema)
        .parse(
          await apiUser.get<unknown>(`/compliance/padroes/${padraoId}/sugestoes`, { params: { idCliente } }),
        ),
  })
}

function invalidarDoCliente(client: QueryClient, idCliente: string) {
  return Promise.all(
    ['compliance-sugestoes', 'compliance-questionario', 'compliance-evidencias'].map((recurso) =>
      client.invalidateQueries({ queryKey: chaveDeQuery(idCliente, recurso) }),
    ),
  )
}

/**
 * 🔒 BR-25 — só por clique. Assíncrono desde o contrato do Tiago (19/10/2026):
 * `POST …/analisar` com `Idempotency-Key` → 202 `{idExecucao}` → polling em
 * `GET …/sugestoes/execucoes/{idExecucao}`. Só resolve quando o job termina, e
 * só então a lista é invalidada (o mesmo achado do Discovery-IA de 13/10).
 * Um backend ainda sem a análise responde 501 `ia_indisponivel` — a tela trata.
 */
export function analisarSugestoesPorIa(idCliente: string, padraoId: string) {
  const base = `/compliance/padroes/${encodeURIComponent(padraoId)}/sugestoes`
  return executarJobDeIa({
    idCliente,
    disparar: (chave) =>
      apiUser.post<unknown>(`${base}/analisar`, undefined, {
        params: { idCliente },
        headers: { 'Idempotency-Key': chave },
      }),
    caminhoDoStatus: (idExecucao) => `${base}/execucoes/${encodeURIComponent(idExecucao)}`,
    mensagemDeDemora:
      'A análise por IA está demorando mais do que o esperado. As sugestões podem aparecer sozinhas em instantes — recarregue a tela.',
  })
}

export function useAnalisarSugestoes(idCliente: string, padraoId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => analisarSugestoesPorIa(idCliente, padraoId),
    onSuccess: () => void invalidarDoCliente(client, idCliente),
  })
}

export function useDecidirSugestao(idCliente: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (dados: { id: string; estado: 'aceita' | 'descartada' }) =>
      sugestaoSchema.parse(
        await apiUser.patch<unknown>(`/compliance/sugestoes/${dados.id}`, { estado: dados.estado }, {
          params: { idCliente },
        }),
      ),
    onSuccess: () => void invalidarDoCliente(client, idCliente),
  })
}

export function useAceitarTodas(idCliente: string, padraoId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async () =>
      z
        .object({ aceitas: z.number().int() })
        .parse(
          await apiUser.post<unknown>(`/compliance/padroes/${padraoId}/sugestoes/aceitar-todas`, undefined, {
            params: { idCliente },
          }),
        ),
    onSuccess: () => void invalidarDoCliente(client, idCliente),
  })
}

// ── Documentos & evidências (por cliente) ───────────────────────────────────
export function useEvidencias(idCliente: string, busca: string) {
  const termo = busca.trim()
  return useQuery({
    queryKey: chaveDeQuery(idCliente, 'compliance-evidencias', termo),
    queryFn: async () =>
      z.array(evidenciaSchema).parse(
        await apiUser.get<unknown>('/documentos/evidencias', {
          params: termo ? { idCliente, busca: termo } : { idCliente },
        }),
      ),
  })
}

export type VinculoPedido = { padraoId: string; perguntaId: string }

export type NovaEvidencia =
  | { tipo: 'arquivo'; arquivo: File; nome: string; observacoes: string; vinculos: VinculoPedido[] }
  | { tipo: 'link'; urlExterna: string; nome: string; observacoes: string; vinculos: VinculoPedido[] }

export function useCriarEvidencia(idCliente: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (dados: NovaEvidencia) => {
      const observacoes = dados.observacoes.trim() || undefined
      if (dados.tipo === 'link') {
        return evidenciaSchema.parse(
          await apiUser.post<unknown>(
            '/documentos/evidencias',
            { nome: dados.nome, tipo: 'link', urlExterna: dados.urlExterna, observacoes, vinculos: dados.vinculos },
            { params: { idCliente } },
          ),
        )
      }
      const enviado = await enviarArquivo(dados.arquivo, async (meta) =>
        urlDeUploadSchema.parse(
          await apiUser.post<unknown>('/documentos/evidencias/upload-url', meta, { params: { idCliente } }),
        ),
      )
      return evidenciaSchema.parse(
        await apiUser.post<unknown>(
          '/documentos/evidencias',
          {
            nome: dados.nome,
            tipo: enviado.tipo,
            chave: enviado.chave,
            tamanhoBytes: enviado.tamanhoBytes,
            observacoes,
            vinculos: dados.vinculos,
          },
          { params: { idCliente } },
        ),
      )
    },
    onSuccess: () => void invalidarDoCliente(client, idCliente),
  })
}

export function useAtualizarVinculos(idCliente: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (dados: { id: string; vinculos: VinculoPedido[] }) =>
      evidenciaSchema.parse(
        await apiUser.put<unknown>(
          `/documentos/evidencias/${dados.id}/vinculos`,
          { vinculos: dados.vinculos },
          { params: { idCliente } },
        ),
      ),
    onSuccess: () => void invalidarDoCliente(client, idCliente),
  })
}

export function useExcluirEvidencia(idCliente: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await apiUser.delete(`/documentos/evidencias/${id}`, { params: { idCliente } })
    },
    onSuccess: () => void invalidarDoCliente(client, idCliente),
  })
}

/**
 * A URL de leitura, pedida NA HORA do clique. Nunca cacheada: expira em 5 min, e
 * uma URL assinada guardada no cache abriria um link morto.
 */
export async function obterUrlDeDownload(idCliente: string, idEvidencia: string): Promise<string> {
  return urlDeDownloadSchema.parse(
    await apiUser.get<unknown>(`/documentos/evidencias/${idEvidencia}/download-url`, { params: { idCliente } }),
  ).url
}
